import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { Button } from '@/components/shadcn/button'
import { DropdownMenuItem } from '@/components/shadcn/dropdown-menu'
import { SheetTrigger } from '@/components/shadcn/sheet'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { CellContext } from '@tanstack/react-table'
import { DataTableV2Action, DataTableV2DataRow } from '../types'
import { CollaborationMenuItemV2 } from './CollaborationMenuItemV2/CollaborationMenuItemV2'
import { NavigationMenuItem } from './NavigationMenuItem'
import { SheetContentAction } from './SheetContentAction'
import { SubmitActionNoConfirmation } from './SbmitActionNoConfirmation'
import { SubmitActionWithConfirmDialog } from './SumbitActionWithConfirmDialog'
import { UploadFilesMenuItemV2 } from './UploadFilesMenuItemV2'
import { UploadTemplateMenuItemV2 } from './UploadTemplateMenuItemV2'

type RowActionDisplayAs = 'dropdown-item' | 'icon-button'

type RowActionV2Props<T, V> = {
  action: DataTableV2Action
  info: CellContext<T, V>
  displayAs: RowActionDisplayAs
}

export function RowActionV2<T, V>({ action, info, displayAs }: RowActionV2Props<T, V>) {
  const formData = info.row.original as DataTableV2DataRow
  const isIconButton = displayAs === 'icon-button'

  switch (action.type) {
    case 'sheet-content': {
      const trigger = isIconButton ? (
        <TooltipButton tooltip={action.label}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="size-6" disabled={action.isDisabled}>
              <DynamicIcon iconName={action.icon || ''} lazyLoad={false} />
              <span className="sr-only">{action.label}</span>
            </Button>
          </SheetTrigger>
        </TooltipButton>
      ) : (
        <SheetTrigger asChild>
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem key={action.id} onSelect={(e) => e.preventDefault()} disabled={action.isDisabled}>
              <div className="flex items-center gap-2">
                <DynamicIcon iconName={action.icon || ''} lazyLoad={false} className="size-3" />
                <span>{action.label}</span>
              </div>
            </DropdownMenuItem>
          </div>
        </SheetTrigger>
      )

      return (
        <SheetContentAction
          key={action.id}
          action={action}
          trigger={trigger}
          formData={formData}
          isDisabled={action.isDisabled ?? false}
        />
      )
    }
    case 'collaboration': {
      return <CollaborationMenuItemV2 key={action.id} action={action} info={info} displayAs={displayAs} />
    }
    case 'upload-template': {
      return <UploadTemplateMenuItemV2 key={action.id} action={action} info={info} displayAs={displayAs} />
    }
    case 'upload-files': {
      return <UploadFilesMenuItemV2 key={action.id} action={action} info={info} displayAs={displayAs} />
    }
    case 'submit': {
      if (action.requireConfirmation) {
        return (
          <SubmitActionWithConfirmDialog
            key={action.id}
            action={action}
            formData={formData}
            actionPosition={isIconButton ? 'table' : 'row'}
          />
        )
      }
      return (
        <SubmitActionNoConfirmation
          key={action.id}
          action={action}
          formData={formData}
          actionPosition={isIconButton ? 'table' : 'row'}
        />
      )
    }
    case 'navigate': {
      return <NavigationMenuItem key={action.id} action={action} info={info} displayAs={displayAs} />
    }
    default:
      return null
  }
}
